export type LeadEmailTemplate = {
  id: string;
  name: string;
  description: string;
  subject: string;
  html: string;
  category: 'announcement' | 'update' | 'event' | 'professional';
};

/** Starter newsletter layouts staff can insert into the campaign composer. */
export const LEAD_EMAIL_TEMPLATES: LeadEmailTemplate[] = [
  {
    id: 'announcement-hero',
    name: 'Announcement hero',
    description: 'Bold headline with supporting copy and CTA link.',
    category: 'announcement',
    subject: 'Exciting news from HomeCook',
    html: `<h2 style="color:#013847;margin:0 0 12px;">Big news for your kitchen journey</h2>
<p>Hi {{first_name}},</p>
<p>We have an update we think you'll love. HomeCook is expanding access to shared commercial kitchen space and operator resources in your area.</p>
<p><a href="https://homecookapp.com" style="color:#013847;font-weight:600;">Learn more →</a></p>
<p>Best,<br/>The HomeCook Team</p>`,
  },
  {
    id: 'product-update',
    name: 'Product update',
    description: 'Feature highlights with bullet list.',
    category: 'update',
    subject: "What's new at HomeCook",
    html: `<p>Hi {{first_name}},</p>
<p>Here's what's new on HomeCook this month:</p>
<ul>
  <li><strong>Smarter booking</strong> — reserve kitchen time in fewer clicks.</li>
  <li><strong>Compliance hub</strong> — track licenses and documents in one place.</li>
  <li><strong>Community insights</strong> — see how operators like you are growing.</li>
</ul>
<p>Log in to explore the updates, or reply with questions — we're here to help.</p>
<p>Cheers,<br/>HomeCook</p>`,
  },
  {
    id: 'event-invite',
    name: 'Event invitation',
    description: 'Event date block with RSVP-style CTA.',
    category: 'event',
    subject: "You're invited — HomeCook community session",
    html: `<h2 style="color:#013847;">Join us for a live kitchen operator session</h2>
<p>Hi {{first_name}},</p>
<p>We're hosting a virtual session for food entrepreneurs and kitchen partners. Bring your questions about licensing, production scheduling, and scaling your business.</p>
<p><strong>When:</strong> Thursday, 6:00 PM ET<br/>
<strong>Where:</strong> Online — link sent after RSVP</p>
<p><a href="https://homecookapp.com" style="background:#013847;color:#fff;padding:10px 18px;border-radius:8px;text-decoration:none;display:inline-block;">Reserve your spot</a></p>
<p>See you there,<br/>The HomeCook Team</p>`,
  },
  {
    id: 'joint-seminar-invite',
    name: 'Joint seminar invite',
    description: 'Education-first 45–55 minute HomeCook × AiriWheels seminar invite. CTA goes to the public opt-in landing, not a bulk referral.',
    category: 'event',
    subject: "You're invited — HomeCook × AiriWheels financial ops seminar",
    html: `<h2 style="color:#013847;margin:0 0 12px;">Join a 50-minute session for commercially active kitchens</h2>
<p>Hi {{first_name}},</p>
<p>HomeCook and AiriWheels are hosting a live working session on receipts-to-PnL, margin-by-dish, and inventory signals — built for restaurants, food trucks, and caterers already generating sales.</p>
<p><strong>Length:</strong> 45–55 minutes<br/>
<strong>Format:</strong> Education first, optional check-fit afterward<br/>
<strong>Who it's for:</strong> Operators with active sales who want a clearer financial picture</p>
<p>This invite is not a product pitch dump. If the session is useful, you can opt in on the landing page and we will intro you with a joint-seminar referral code.</p>
<p style="margin:24px 0;"><a href="https://resources.homecookapp.com/funding/?channel=joint_seminar" style="background:#013847;color:#fff;padding:12px 20px;border-radius:8px;text-decoration:none;display:inline-block;font-weight:600;">Reserve your spot</a></p>
<p>See you there,<br/>HomeCook + AiriWheels</p>`,
  },
  {
    id: 'plain-professional',
    name: 'Plain professional',
    description: 'Minimal letter-style note for quick outreach.',
    category: 'professional',
    subject: 'Following up from HomeCook',
    html: `<p>Hi {{first_name}},</p>
<p>I wanted to reach out personally and see how things are going with your food business. If you're exploring shared kitchen space, licensing support, or community resources, we'd love to connect.</p>
<p>Reply anytime — or book time with our team when it works for you.</p>
<p>Best regards,<br/>HomeCook Partnerships</p>`,
  },
  {
    id: 'seasonal-promo',
    name: 'Seasonal spotlight',
    description: 'Warm promo with highlighted offer block.',
    category: 'announcement',
    subject: 'A seasonal opportunity for {{first_name}}',
    html: `<p>Hi {{first_name}},</p>
<p>As the season picks up, many operators are looking for flexible kitchen access without long-term leases. HomeCook makes it easier to produce, comply, and grow on your schedule.</p>
<blockquote>
  <p><strong>Spotlight:</strong> Priority onboarding for qualified food entrepreneurs this month.</p>
</blockquote>
<p><a href="https://homecookapp.com">Start your application →</a></p>
<p>Warmly,<br/>HomeCook</p>`,
  },
  {
    id: 'resource-provider',
    name: 'Resource provider outreach',
    description: 'B2B tone for kitchen and resource partners.',
    category: 'professional',
    subject: 'Partner with HomeCook — shared kitchen network',
    html: `<p>Hi {{first_name}},</p>
<p>HomeCook connects commercial kitchen operators with food entrepreneurs who need compliant, flexible production space. If you manage kitchen capacity or related resources, we'd value a conversation about listing with us.</p>
<p>Our team handles lead qualification, onboarding support, and operator communications so you can focus on utilization.</p>
<p><a href="https://homecookapp.com">Explore partnership options →</a></p>
<p>Thank you,<br/>HomeCook Business Development</p>`,
  },
  {
    id: 'visual-hero-newsletter',
    name: 'Visual hero newsletter',
    description: 'Hero image block, headline, CTA button, and footer note.',
    category: 'announcement',
    subject: 'Updates for {{first_name}} from HomeCook',
    html: `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;">
  <tr><td><img src="{{hero_image_url}}" alt="Hero image" width="600" style="width:100%;max-width:600px;border-radius:12px 12px 0 0;display:block;" /></td></tr>
  <tr><td style="padding:24px;background:#ffffff;color:#2C3E50;">
    <img src="{{logo_url}}" alt="Logo" width="120" style="display:block;margin-bottom:16px;" />
    <h2 style="color:#013847;margin:0 0 12px;">Big news for operators in {{city}}</h2>
    <p>Hi {{first_name}},</p>
    <p>HomeCook is expanding shared kitchen access, compliance support, and community resources for food entrepreneurs like you.</p>
    <p style="margin:24px 0;"><a href="https://homecookapp.com" style="background:#013847;color:#fff;padding:12px 20px;border-radius:8px;text-decoration:none;display:inline-block;font-weight:600;">Explore what's new</a></p>
    <p style="color:#737373;font-size:13px;">Replace placeholder images with your brand assets before sending.</p>
  </td></tr>
</table>`,
  },
  {
    id: 'visual-feature-grid',
    name: 'Visual feature grid',
    description: 'Two-column feature highlights with icons and CTA.',
    category: 'update',
    subject: "What's new for {{business_name}}",
    html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;color:#2C3E50;">
  <img src="{{hero_image_url}}" alt="Newsletter hero" width="600" style="width:100%;border-radius:12px;margin-bottom:20px;" />
  <p>Hi {{first_name}},</p>
  <p>Here's what's new for operators in your region:</p>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
    <tr>
      <td width="50%" valign="top" style="padding:12px;">
        <img src="{{feature_image_url}}" alt="Feature" width="240" style="width:100%;border-radius:8px;margin-bottom:8px;" />
        <strong>Flexible booking</strong><br/>Reserve kitchen time in fewer clicks.
      </td>
      <td width="50%" valign="top" style="padding:12px;">
        <img src="{{cta_image_url}}" alt="Compliance" width="240" style="width:100%;border-radius:8px;margin-bottom:8px;" />
        <strong>Compliance hub</strong><br/>Track licenses and documents in one place.
      </td>
    </tr>
  </table>
  <p><a href="https://homecookapp.com" style="color:#013847;font-weight:600;">See all updates →</a></p>
</div>`,
  },
  {
    id: 'visual-community-spotlight',
    name: 'Community spotlight',
    description: 'Warm spotlight layout with quote block and image.',
    category: 'announcement',
    subject: 'Spotlight for food entrepreneurs in {{city}}',
    html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#EFF8FA;border-radius:12px;overflow:hidden;border:1px solid #CFE8EF;">
  <img src="{{hero_image_url}}" alt="Community spotlight" width="600" style="width:100%;display:block;" />
  <div style="padding:24px;color:#2C3E50;">
    <p>Hi {{first_name}},</p>
    <p>Operators across {{city}} are scaling production with flexible shared kitchen access through HomeCook.</p>
    <blockquote style="margin:16px 0;padding:12px 16px;border-left:4px solid #5DB6CE;background:#fff;border-radius:8px;">
      <strong>Spotlight:</strong> Priority onboarding for qualified food entrepreneurs this month.
    </blockquote>
    <img src="{{feature_image_url}}" alt="Feature image" width="520" style="width:100%;border-radius:8px;margin:16px 0;" />
    <p><a href="https://homecookapp.com" style="background:#013847;color:#fff;padding:10px 18px;border-radius:8px;text-decoration:none;display:inline-block;">Learn more</a></p>
  </div>
</div>`,
  },
];
